import fetchAllBrands from '@dehaat/kisan-app-bl/api/fetchAllBrands';
import fetchAllCropsV2, { FETCH_CROPS } from '@dehaat/kisan-app-bl/api/fetchAllCropsV2';
import Categories from '@dehaat/kisan-app-bl/models/CategoriesV2';
import Crop from '@dehaat/kisan-app-bl/models/CropV2';
import { Brand } from '@dehaat/kisan-app-bl/models/Product';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect, useMemo, useState } from 'react';
import AppBackButton from '@/components/AppBackButton';
import MyCart from '@/components/cart/MyCart';
import CheckoutPopup from '@/components/CheckoutPopup';
import MagnifyingGlass from '@/components/icons/MagnifyingGlass';
import LazyImage from '@/components/LazyImage';
import CurrentLocation from '@/components/navigation/CurrentLocation';
import Profile from '@/components/navigation/Profile';
import ProductFilter from '@/components/products/ProductFilter';
import ProductResults from '@/components/products/ProductResults';
import { COOKIE_MAX_AGE_1YEAR, CURRENT_VENDOR_KEY, DC_ID, IS_PREBOOK_FLOW, LAT_LNG_COOKIE_NAME, NAVIGATION_FROM_APP, NAVIGATION_FROM_APP_CODE, PRODUCTS_QUERY_OPEN, REDIRECT_URI_KEY, VENDOR_ID_COOKIE_NAME } from '@/constants/common';
import { FilterTypes } from '@/constants/filterTypes';
import { CartContext } from '@/context/CartProvider';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import useIsMobile from '@/hooks/useIsMobile';
import useSearchProducts, { createSearchProductQuery } from '@/hooks/useSearchProducts';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import scrollStyles from '@/styles/HideScrollbar.module.css';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import { ProductListTypeEnum } from '@/utils/analytics/types';
import fetchAllCategoriesV2 from '@/utils/api/fetchallCategories';
import fetchAllProducts from '@/utils/api/fetchAllProducts';
import submitCampaignDetails from '@/utils/api/submitCampaignDetails';
import { isCartEmpty, isOnlyServicesInCart } from '@/utils/cart';
import { getCookieValue, getLocalStorageKey, isDigiAcreUser, isFromVrApp, resolveAsString, sendBackEventToAndroid, setLocalStorageKey } from '@/utils/helper';
import getCategoryFilterQuery from '@/utils/helpers/getCategoryFilterQuery';
import parseQuery from '@/utils/helpers/parseQuery';
interface Props {
  cropInfo?: Crop;
  categories: Categories[];
}
const CropSpecificProductListing = ({
  cropInfo,
  categories
}: Props) => {
  const {
    t
  } = useTranslation('products');
  const {
    isMobile,
    isWindowLoading
  } = useIsMobile();
  const {
    asPath,
    back,
    query,
    replace,
    pathname,
    locale,
    push
  } = useRouter();
  const {
    cropSlug,
    c,
    sp,
    fcp
  } = query;
  const {
    cartItems,
    isPrebookFlow
  } = useContext(CartContext);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(c ? Number(c) : categories.length > 0 ? categories[0].id : undefined);
  const [count, setTotalCount] = useState<number>(0);
  const vendorId = getCookieValue(VENDOR_ID_COOKIE_NAME) as string;
  const [prebookCategories, setPrebookCategories] = useState<Categories[]>();
  const sidebarCategories = isPrebookFlow && prebookCategories ? prebookCategories : categories;
  const latLong = getCookieValue(LAT_LNG_COOKIE_NAME) as string;
  const isDAUser = isDigiAcreUser();
  const fromApp = useMemo(() => Boolean(getCookieValue(NAVIGATION_FROM_APP)) || Boolean(getCookieValue(NAVIGATION_FROM_APP_CODE)) || isDAUser, [isDAUser]);
  const {
    isEnabled
  } = useFeatureFlags();
  const isVR = isFromVrApp(getCookieValue(NAVIGATION_FROM_APP_CODE));
  const isNewSearch = isVR || isEnabled('enable_new_search', true);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const {
    searchedProducts,
    searchingProducts,
    infiniteScrollRef,
    appliedBrandIds,
    appliedCategoryIds,
    appliedCropIds
  } = useSearchProducts(true, 10, cropInfo?.id?.toString(), selectedCategory ? selectedCategory.toString() : '');
  const cropSlugEnc = encodeURIComponent(resolveAsString(cropSlug));
  useEffect(() => {
    if (selectedCategory) {
      const queryString = asPath.split('?')[1] || '';
      const updatedQuery = getCategoryFilterQuery(queryString, selectedCategory.toString());
      replace({
        pathname: `/products/crops/${cropSlugEnc}`,
        query: updatedQuery
      }, undefined, {
        shallow: true
      });
    }
  }, [selectedCategory]);

  // The prebook flow lives in localStorage; getServerSideProps only sees it
  // while `prebook=true` is in the URL, and the category effect above drops
  // that param. Fetch the prebook sidebar here so it survives in-app
  // navigation, reloads and page-data refreshes.
  useEffect(() => {
    if (!isPrebookFlow || query.prebook === 'true') return;
    let ignore = false;
    fetchAllCategoriesV2({
      crop_ids: resolveAsString(cropSlug),
      lang: locale,
      vendor_id: vendorId && vendorId !== 'null' ? vendorId : undefined,
      isprebook: true
    }).then(response => {
      if (!ignore && response) setPrebookCategories(response);
    });
    return () => {
      ignore = true;
    };
  }, [isPrebookFlow, query.prebook, cropSlug, locale, vendorId]);
  useEffect(() => {
    if (!selectedCategory && sidebarCategories.length > 0) {
      setSelectedCategory(sidebarCategories[0].id);
    }
  }, [sidebarCategories]);
  useEffect(() => {
    if (!cropInfo?.id?.toString()) return;
    const fetchCropCount = async () => {
      const isprebook = !!getLocalStorageKey(IS_PREBOOK_FLOW);
      try {
        const params = {
          vendor_id: vendorId || '',
          page_size: 1,
          crop_id: cropInfo?.id?.toString(),
          lang: locale ? locale : 'en',
          isprebook,
          iscount: true,
          brand_id: '',
          category_id: '',
          collection_id: ''
        };
        const queryString = createSearchProductQuery(params);
        const response = await fetchAllProducts({
          query: queryString
        });
        setTotalCount(response.count || 0);
      } catch (error) {
        console.error('Error fetching category count:', error);
        setTotalCount(0);
      }
    };
    fetchCropCount();
  }, [cropInfo?.id?.toString()]);
  const handleCategoryClick = (id: number) => {
    setSelectedCategory(id);
    window.scrollTo(0, 0);
  };
  useEffect(() => {
    const {
      requester_id,
      farmer_ph_no
    } = parseQuery(query, ['requester_id', 'farmer_ph_no']);
    if (requester_id && farmer_ph_no) submitCampaignDetails({
      requester_id,
      farmer_ph_no
    });
  }, [query]);
  useEffect(() => {
    if (cropInfo?.id && cropInfo?.name) {
      const eventNames = {
        cropId: cropInfo.id,
        cropName: cropInfo.name
      };
      trackCustomEvent(CustomEventTypes.USER_LANDED_ON_SHOP_BY_CROP, JSON.stringify(eventNames));
    }
  }, [cropInfo]);
  useEffect(() => {
    const vendorData = localStorage.getItem(CURRENT_VENDOR_KEY);
    const dcId = vendorData ? JSON.parse(vendorData)?.entity_id : undefined;
    if (dcId) {
      document.cookie = `${DC_ID}=${dcId}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
    }
  }, []);
  useEffect(() => {
    const getBrands = async () => {
      const brands = await fetchAllBrands(locale, latLong, vendorId);
      return brands;
    };
    getBrands().then(response => setBrands(response));
  }, [latLong, locale, vendorId]);
  return <main className="relative bg-neutral-10 h-screen" data-sentry-component="CropSpecificProductListing" data-sentry-source-file="[cropSlug].tsx">
      <div className="top-0 left-0 z-50 fixed flex justify-between items-center bg-white shadow-[0_6px_20px_rgba(0,0,0,0.05)] px-4 py-4 w-full">
        <div className="flex items-center">
          <AppBackButton onBackClick={() => {
          if (fcp) {
            sendBackEventToAndroid(window);
          }
          if (isOnlyServicesInCart(cartItems)) {
            setLocalStorageKey(IS_PREBOOK_FLOW, false);
          }
          // if user is going back from prebook flow (crop listing page),
          // redirect to activation page with tab 2
          sp ? replace('/shri-program/activation?tab=2') : back();
        }} data-sentry-element="AppBackButton" data-sentry-source-file="[cropSlug].tsx" />
          <CurrentLocation showAddressDetails={false} fromScreen={asPath} data-sentry-element="CurrentLocation" data-sentry-source-file="[cropSlug].tsx" />

          <div className="relative ml-4 w-8 h-8">
            <LazyImage alt={cropInfo?.app_display_name || resolveAsString(cropSlug)} fallBackIcon={<div className="flex justify-center items-center bg-neutral-300 rounded-full w-8 h-8 overflow-hidden">
                  <span className="text-xl">
                    {cropInfo?.app_display_name[0] || resolveAsString(cropSlug)}
                  </span>
                </div>} imageUrl={cropInfo?.image_url} className="rounded-full w-8 h-8 object-cover" data-sentry-element="LazyImage" data-sentry-source-file="[cropSlug].tsx" />
          </div>
          <div className="ml-2">
            <h1 className="font-nato-semibold text-sm capitalize">
              {cropInfo?.app_display_name || resolveAsString(cropSlug)}
            </h1>
          </div>
        </div>
        <div className="flex gap-4">
          {!isWindowLoading && !isMobile && <MyCart />}

          <div className="p-2 border border-neutral-20 rounded-xl h-fit" onClick={() => {
          !isNewSearch ? replace(`/?so=${PRODUCTS_QUERY_OPEN}&${REDIRECT_URI_KEY}=${encodeURIComponent(asPath)}`) : push(`/s?`);
        }}>
            <MagnifyingGlass className="fill-transparent stroke-neutral-80 w-6 h-6" role="presentation" aria-hidden="true" data-sentry-element="MagnifyingGlass" data-sentry-source-file="[cropSlug].tsx" />
          </div>
          {!fromApp && <Profile />}
        </div>
      </div>

      <section className={`h-screen w-full m-auto pt-20 bg-white flex flex-col relative bg-gray-10 ${isMobile && !isCartEmpty(cartItems) ? 'pb-16' : ''} ${isMobile ? 'pt-[74px]' : 'pt-[84px]'}`}>
        <ProductFilter appliedBrandIds={appliedBrandIds} appliedCategoryIds={appliedCategoryIds} appliedCropIds={appliedCropIds} brands={brands} pathname={`/products/crops/${cropSlug}`} filterTypesToHide={[FilterTypes.CROPS, FilterTypes.CATEGORY]} defaultHighlighted={FilterTypes.BRANDS} productCount={count || 0} data-sentry-element="ProductFilter" data-sentry-source-file="[cropSlug].tsx" />
        <div className={`flex h-full w-full overflow-hidden relative`}>
          <div className={`max-w-[22%] overflow-scroll bg-white ${scrollStyles.scroll}  `}>
            {sidebarCategories?.map(({
            id,
            name,
            image_url,
            app_display_name
          }) => <div onClick={() => handleCategoryClick(id)} key={id} className={`p-4 flex flex-col justify-center items-center relative ${id === selectedCategory ? 'bg-primary-10' : ''}`}>
                  {id === selectedCategory && <div className="left-0 absolute bg-primary-100 rounded-lg w-1 h-10" />}
                  <div className="relative mb-2 w-12 h-12">
                    <LazyImage alt={name} fallBackIcon={<div className="flex justify-center items-center bg-neutral-300 rounded-full w-12 h-12 overflow-hidden">
                          <span className="text-4xl">{name[0]}</span>
                        </div>} imageUrl={image_url} className="rounded-full w-12 h-12 object-cover" />
                  </div>
                  <p className="text-neutral-60 text-xs text-center">
                    {app_display_name}
                  </p>
                </div>)}
          </div>
          <div className="flex-1 h-full overflow-scroll">
            <ProductResults products={searchedProducts} loading={searchingProducts} cartEmpty={true} nextLoaderRef={infiniteScrollRef} noResults={<span className="inline-block flex-1 mt-4 px-4 text-center">
                  {t('no_filtered_products')}
                </span>} listType={ProductListTypeEnum.PRODUCT} data-sentry-element="ProductResults" data-sentry-source-file="[cropSlug].tsx" />
          </div>
        </div>
      </section>
      <CheckoutPopup data-sentry-element="CheckoutPopup" data-sentry-source-file="[cropSlug].tsx" />
    </main>;
};
export const getServerSideProps: GetServerSideProps = async ({
  locale,
  query,
  req: {
    cookies
  }
}) => {
  let allCrops: Crop[] = [];
  const vendorId = cookies[VENDOR_ID_COOKIE_NAME] !== 'null' ? cookies[VENDOR_ID_COOKIE_NAME] : undefined;
  let categories: Categories[] = [];
  try {
    const [cropsResult, categoriesResult] = await Promise.allSettled([fetchAllCropsV2(locale, FETCH_CROPS.ALL_CROPS), fetchAllCategoriesV2({
      crop_ids: resolveAsString(query.cropSlug),
      lang: locale,
      vendor_id: vendorId,
      // Same flag as /hyperlocal/v1/products, so prebook-only vendors get
      // their categories too
      ...(query.prebook === 'true' && {
        isprebook: true
      })
    })]);
    if (cropsResult.status !== 'fulfilled') {
      throw new Error('Failed to fetch crops');
    }
    const crops = cropsResult.value;
    const cropCategories = categoriesResult.status === 'fulfilled' ? categoriesResult.value : [];
    crops?.forEach(category => allCrops = category.crops.reduce((prev, crop) => prev.concat(crop), allCrops));
    const cropInfo = allCrops.find(({
      slug
    }) => slug.slug === query.cropSlug) || null;
    if (cropCategories) {
      categories = cropCategories;
    }
    const title = cropInfo ? `${cropInfo.app_display_name} Products` : `${resolveAsString(query.brandSlug)} Products`;
    return {
      props: {
        cropInfo,
        categories,
        title
      }
    };
  } catch {
    return {
      redirect: {
        destination: '/500',
        permanent: false
      }
    };
  }
};
export default CropSpecificProductListing;